import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, FileText, Info, Mail, Pencil, Send } from 'lucide-react';

import { PageHeader } from '@/ui/PageHeader';
import { Spinner } from '@/ui/Spinner';
import { EmptyState } from '@/ui/EmptyState';
import { EstadoDaTela } from '@/ui/EstadoDaTela';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { StatsGrid } from '@/ui/StatsGrid';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Card, CardContent } from '@/ui/base/card';
import { Button } from '@/ui/base/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';
import { TAMANHO_PAGINA_PADRAO } from '@/ui/tamanhoDePagina';
import {
  PERIODOS,
  ROTULO_DA_SITUACAO,
  ROTULO_DO_PERIODO,
  SITUACOES_DO_ENVIO,
  rotuloDoEvento,
  type EmailEnviado,
  type Periodo,
  type SituacaoDoEnvio,
} from './contrato';
import { useEmailsEnviados } from './dados';
import { TEMPLATES_EMAIL_MOCK } from './dadosMock';

/** Espera depois da última tecla antes de buscar: cada busca é uma consulta registrada. */
const ESPERA_DA_BUSCA_MS = 400;

function selo(situacao: SituacaoDoEnvio) {
  const rotulo = ROTULO_DA_SITUACAO[situacao];
  switch (situacao) {
    case 'ENVIADO':
      return <Badge>{rotulo}</Badge>;
    case 'FALHOU':
      return <Badge variant="destructive">{rotulo}</Badge>;
    case 'EM_ENVIO':
      return <Badge variant="secondary">{rotulo}</Badge>;
    default:
      return <Badge variant="outline">{rotulo}</Badge>;
  }
}

function dataEHora(iso: string): string {
  const data = new Date(iso);
  // O protótipo escreve `03/09/2026 08:00`: sem vírgula e sem segundos.
  return `${data.toLocaleDateString('pt-BR')} ${data.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

/** O valor só "assenta" depois de `ms` sem mudar. */
function useValorAssentado(valor: string, ms: number): string {
  const [assentado, setAssentado] = useState(valor);
  useEffect(() => {
    const tempo = window.setTimeout(() => setAssentado(valor), ms);
    return () => window.clearTimeout(tempo);
  }, [valor, ms]);
  return assentado;
}

/**
 * Aba "Monitoramento": os e-mails que o sistema enviou, lidos da API. A fila e
 * o registro de envio são do banco; o endereço do destinatário chega inteiro
 * e a consulta fica registrada como acesso a dado pessoal.
 */
function AbaMonitoramento() {
  const [busca, setBusca] = useState('');
  const [situacao, setSituacao] = useState('');
  const [evento, setEvento] = useState('');
  const [periodo, setPeriodo] = useState<Periodo>('7d');
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState<number>(TAMANHO_PAGINA_PADRAO);
  const buscaAssentada = useValorAssentado(busca, ESPERA_DA_BUSCA_MS);

  const consulta = useEmailsEnviados({
    periodo,
    situacao,
    evento,
    busca: buscaAssentada,
    pagina,
    tamanho: porPagina,
  });
  const lista = consulta.data;

  const colunas: DataTableColumn<EmailEnviado>[] = [
    {
      id: 'tipo',
      header: 'Tipo',
      cell: (r) => <span className="text-sm font-medium">{rotuloDoEvento(r.evento)}</span>,
    },
    {
      id: 'destinatario',
      header: 'Destinatário',
      cell: (r) => (
        <div>
          <div className="text-sm">{r.destinatarioNome ?? '—'}</div>
          <div className="text-xs text-muted-foreground">{r.destinatarioEmail ?? '—'}</div>
        </div>
      ),
    },
    {
      id: 'data',
      header: 'Data',
      cell: (r) => <span className="text-sm text-muted-foreground">{dataEHora(r.criadoEm)}</span>,
      sortKey: (r) => new Date(r.criadoEm).getTime(),
    },
    {
      id: 'erro',
      header: 'Erro',
      cell: (r) =>
        r.erro ? (
          <span className="text-xs text-destructive-text">{r.erro}</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    // Status por último: é a ordem do protótipo (Tipo, Destinatário, Data,
    // Erro, Status).
    { id: 'status', header: 'Status', cell: (r) => selo(r.situacao) },
  ];

  const campos: FilterFieldConfig[] = [
    {
      key: 'busca',
      label: 'Busca',
      type: 'search',
      placeholder: 'Buscar destinatário…',
      flex: true,
    },
    {
      key: 'situacao',
      label: 'Status',
      type: 'select',
      options: SITUACOES_DO_ENVIO.map((valor) => ({
        value: valor,
        label: ROTULO_DA_SITUACAO[valor],
      })),
    },
    {
      key: 'evento',
      label: 'Tipo',
      type: 'select',
      // Só os tipos que aparecem no período: filtro por tipo sem e-mail não tem o que mostrar.
      options: (lista?.eventos ?? []).map((codigo) => ({
        value: codigo,
        label: rotuloDoEvento(codigo),
      })),
    },
  ];

  const resumo = lista?.resumo;

  return (
    <div className="space-y-6">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Cada consulta com destinatários fica registrada, por proteção de dados pessoais. Só
          e-mails com endereço de destino entram na lista.
        </AlertDescription>
      </Alert>

      <EstadoDaTela
        carregando={consulta.isPending}
        erro={consulta.error}
        aoTentarDeNovo={() => void consulta.refetch()}
        descricao="o monitoramento de e-mails"
        esqueleto={{ colunas: 5 }}
      >
        <div className="space-y-6">
          <StatsGrid
            stats={[
              { title: 'Total', value: resumo?.total ?? 0, icon: Mail },
              { title: 'Na fila', value: resumo?.naFila ?? 0, icon: Clock },
              { title: 'Enviados', value: resumo?.enviados ?? 0, icon: Send },
              { title: 'Falhas', value: resumo?.falhas ?? 0, icon: AlertTriangle },
            ]}
          />

          <div className="flex gap-1" role="group" aria-label="Período">
            {PERIODOS.map((p) => (
              <Button
                key={p}
                variant={periodo === p ? 'default' : 'outline'}
                size="sm"
                aria-pressed={periodo === p}
                onClick={() => {
                  setPeriodo(p);
                  setEvento('');
                  setPagina(1);
                }}
              >
                {ROTULO_DO_PERIODO[p]}
              </Button>
            ))}
          </div>

          <FilterBar
            fields={campos}
            values={{ busca, situacao, evento }}
            onChange={(chave, valor) => {
              if (chave === 'busca') setBusca(valor);
              if (chave === 'situacao') setSituacao(valor);
              if (chave === 'evento') setEvento(valor);
              // Qualquer filtro novo volta para a primeira página.
              setPagina(1);
            }}
            onClear={() => {
              setBusca('');
              setSituacao('');
              setEvento('');
              setPagina(1);
            }}
          />

          <DataTable
            columns={colunas}
            data={lista?.itens ?? []}
            isLoading={consulta.isPlaceholderData}
            total={lista?.total ?? 0}
            page={pagina}
            perPage={porPagina}
            onPageChange={setPagina}
            onPerPageChange={(tamanho) => {
              setPorPagina(tamanho);
              setPagina(1);
            }}
            ariaLabel="Monitoramento de e-mails transacionais"
            emptyIcon={Mail}
            emptyTitle="Nenhuma notificação encontrada"
            emptyDescription="Nenhum e-mail foi registrado com os filtros atuais."
          />
        </div>
      </EstadoDaTela>
    </div>
  );
}

/** Aba "Templates": grade de cartões, um por tipo de e-mail. */
function AbaTemplates() {
  const navigate = useNavigate();
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    const tempo = window.setTimeout(() => setCarregando(false), 200);
    return () => window.clearTimeout(tempo);
  }, []);

  if (carregando) {
    return <Spinner />;
  }

  if (!TEMPLATES_EMAIL_MOCK.length) {
    return (
      <EmptyState
        icon={Mail}
        title="Nenhum template encontrado"
        description="Não há templates de e-mail cadastrados."
      />
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {TEMPLATES_EMAIL_MOCK.map((t) => (
        <Card key={t.id} className="transition-shadow hover:shadow-md">
          <CardContent className="flex flex-col gap-3 p-5">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="truncate font-semibold text-foreground">{t.nome}</h3>
                <p className="mt-0.5 font-mono text-xs text-muted-foreground">{t.tipo}</p>
              </div>
              <Badge variant={t.ativo ? 'default' : 'secondary'} className="shrink-0">
                {t.ativo ? 'Ativo' : 'Inativo'}
              </Badge>
            </div>

            <p className="line-clamp-1 text-sm text-muted-foreground">
              <span className="font-medium">Assunto:</span> {t.assunto}
            </p>

            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Editado em {new Date(t.atualizadoEm).toLocaleString('pt-BR')}
              </span>
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={() => navigate(`/admin/emails/templates/${t.id}`)}
              >
                <Pencil className="h-3.5 w-3.5" />
                Editar
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function EmailsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const abaAtiva = searchParams.get('tab') ?? 'monitoramento';

  return (
    <div className="space-y-6">
      <PageHeader
        title="E-mails Transacionais"
        description="Monitoramento e gestão de templates de e-mail."
      />

      <Tabs value={abaAtiva} onValueChange={(v) => setSearchParams({ tab: v })}>
        <TabsList>
          <TabsTrigger value="monitoramento" className="gap-1.5">
            <Mail className="h-4 w-4" />
            Monitoramento
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-1.5">
            <FileText className="h-4 w-4" />
            Templates
          </TabsTrigger>
        </TabsList>

        <TabsContent value="monitoramento">
          <AbaMonitoramento />
        </TabsContent>

        <TabsContent value="templates">
          <AbaTemplates />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default EmailsPage;
